import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useAtom } from 'jotai/index';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import MyTable from '../../components/MyTable';
import { NICKNAMES_HEADERS } from '../../constants/tables';
import MyInput from '../../components/MyInput';
import { nicknamesFiltersAtom, selectedNicknameAtom } from '../../atoms/nicknamesAtoms';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../api';
import RightArrowBlue from '../../assets/icons/rightArrowBlue.svg';
import TonIcon from '../../assets/icons/ton.svg';
import RightArrow from '../../assets/icons/arrowRight.svg';
import EmptyHeight from '../../components/EmptyHeight';
import convertPrice from '../../helpers/convertPrice';
import MyButton from '../../components/MyButton';
import useInputState from '../../hooks/UseInputState';
import { modalStateAtom } from '../../atoms/globalAtoms';
import SuccessTransactionModal from './components/SuccessTransactionModal';
import usernames from '../Usernames';
import MyLoading from '../../components/MyLoading';
import { selectedNumberAtom } from '../../atoms/numbersAtoms';
import convertPhoneNumber from '../../helpers/convertPhoneNumber';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';

const nonAllowedSymbols = [' ', ','];

function hasNonAllowedSymbols(str) {
  return nonAllowedSymbols.some((symbol) => str.includes(symbol));
}
function NumbersSingle() {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [selectedNickname, setSelectedNickname] = useAtom(selectedNumberAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [offerPrice, setOfferPrice] = useInputState('');
  const [offerError, setOfferError] = useState('');
  const [generateTransactionLoading, setGenerateTransactionLoading] = useState(false);
  const [transactionLink, setTransactionLink] = useState('');
  const [receiverWallet, setReceiverWallet] = useState('');
  const [newUsername, setNewUsername] = useInputState('');
  const [newUsernameLoading, setNewUsernameLoading] = useState(false);
  const [newUsernameSuccess, setNewUsernameSuccess] = useState(false);
  const [newUsernameError, setNewUsernameError] = useState(false);

  const ref1 = useRef(null);
  const ref2 = useRef(null);

  const onPayClick = async () => {
    if (Number(offerPrice) < 15) {
      setOfferError(t(DICTIONARY.minimum15));
      return;
    }
    const data = await sendRequest({
      request: Api.generateTransaction,
      payload: { amount: offerPrice },
      setLoading: setGenerateTransactionLoading,
    });

    if (data?.status !== 'ok') return;

    setTransactionLink(data.transactionLink);
    setReceiverWallet(data.receiverWallet);

    setModalState({
      isOpen: true,
      children: <SuccessTransactionModal
        {...data}
        newUsername={newUsername}
        phoneNumber={selectedNickname.phoneNumber}
        price={offerPrice}
      />,
    });
  };

  useEffect(() => {
    setOfferError('');
    if (Number(offerPrice) > 1000000) {
      setOfferPrice('1000000');
    } else if (Number(offerPrice) < 0) {
      setOfferPrice('0');
    }
  }, [offerPrice]);

  useEffect(() => {
    if (!newUsername?.length) {
      setNewUsernameSuccess(false);
      setNewUsernameLoading(false);
      setNewUsernameError(false);
      return;
    }

    setNewUsernameSuccess(false);
    setNewUsernameLoading(true);

    const timer = setTimeout(() => {
      const isSuccess = newUsername?.length >= 4 && !hasNonAllowedSymbols(newUsername || '');
      setNewUsernameSuccess(isSuccess);
      setNewUsernameError(!isSuccess);
      setNewUsernameLoading(false);

      if (isSuccess) {
        setTimeout(() => {
          if (ref1.current) {
            // ref1.current.blur();
          }
          if (ref2.current) {
            // ref2.current.focus();
          }
        }, 300);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [newUsername]);

  useEffect(() => {
    if (!newUsernameSuccess) {
      setOfferPrice('');
      setOfferError('');
    }
  }, [newUsernameSuccess]);

  useEffect(() => {
    if (!selectedNickname?._id) {
      navigate('/');
    }
  }, [selectedNickname]);

  if (!selectedNickname?._id) return null;
  return (
    <div className={styles.wrapper}>
      <p className={styles.header}>
        {convertPhoneNumber(selectedNickname.phoneNumber)}
      </p>
      {selectedNickname?.price && selectedNickname.price !== '-' ? (
        <>
          <p className={styles.headerInfo}>
            {t(DICTIONARY.hereYouCanNumbers)}
            <Link
              to="/about"
            >
              {t(DICTIONARY.learn)}
              <img src={RightArrowBlue} alt=">" />
            </Link>
          </p>

          <EmptyHeight height={30} />

          <div className={styles.priceRow}>
            <span className={styles.priceRowTitle}>
              {t(DICTIONARY.minimalPrice)}
              :
            </span>
            <img src={TonIcon} alt="V" />
            <span>{convertPrice(selectedNickname.price)}</span>
          </div>
          <p className={styles.priceDesc}>
            {t(DICTIONARY.thisIsJustPriceNumber)}
          </p>

          <EmptyHeight height={50} />

          <div>
            <MyInput
              value={newUsername}
              onChange={setNewUsername}
              placeholder={`${t(DICTIONARY.username).toLowerCase()}...`}
              withCloseIcon={false}
              withLeftIcon={false}
              labelTextTop={t(DICTIONARY.newOwner)}
              refToPass={ref1}
              type="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>

          <div>
            {newUsernameLoading
              ? (
                <>
                  <EmptyHeight height={20} />
                  <MyLoading color="#4fb3ff" width={30} height={30} />
                </>
              )
              : newUsernameSuccess ? (
                <>
                  <EmptyHeight height={20} />
                  <div className={styles.inputsRow}>
                    <div className={styles.inputsItem}>
                      <MyInput
                        withCloseIcon={false}
                        LeftIcon={TonIcon}
                        type="number"
                        value={offerPrice}
                        onChange={setOfferPrice}
                        placeholder="TON..."
                        refToPass={ref2}
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </div>
                    <div className={styles.inputsItem}>
                      <MyButton
                        text={t(DICTIONARY.sendOffer)}
                        onClick={onPayClick}
                        loading={generateTransactionLoading}
                      />
                    </div>
                  </div>
                  <EmptyHeight height={20} />
                  {offerError
                    ? <p className={classNames(styles.errorText, styles.errorTextSecond)}>{offerError}</p>
                    : <p className={styles.infoText}>{t(DICTIONARY.youHave3)}</p>}
                </>
              ) : newUsernameError ? <p className={styles.errorText}>{t(DICTIONARY.usernameNotFound)}</p> : null}
          </div>
        </>
      ) : (
        <p className={styles.headerInfo}>
          {t(DICTIONARY.numberNotInList)}
        </p>
      )}

      <EmptyHeight height={40} />
      <p className={styles.bottomInfo}>
        {t(DICTIONARY.numberCanBe)}
      </p>
    </div>
  );
}

export default NumbersSingle;
